import { useState } from 'react'

import { Box, Text, useConfirmationDialog } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { F0AvatarPerson, F0TagStatus } from '@factorialco/f0-react'
import { F0Select } from '@factorialco/f0-react'
import { Crown, Delete, LockLocked, Person, Suitcase } from '@factorialco/f0-react/icons/app'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import {
  AtsGetHiringTeamDocument,
  AtsHiringRolesEnum,
  AtsJobPostingSettingsHiringTeamFragment,
  AtsUpdateHiringManagerRoleDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

import UpsellImage from './image.png'

interface Props {
  canManageJobPostings: boolean
  currentHiringRole: AtsHiringRolesEnum
  hiringManagers: AtsJobPostingSettingsHiringTeamFragment[]
  hasHiringManagerRolesFeature: boolean
  removeHiringManager: (id: number) => void
  jobId: number
}

interface HiringManagerItemProps {
  hiringManager: AtsJobPostingSettingsHiringTeamFragment
  isCreator: boolean
  currentHiringRole: AtsHiringRolesEnum
  removeHiringManager: (id: number) => void
  firstItem: boolean
  hasHiringManagerRolesFeature: boolean
  canManageJobPostings: boolean
  setUpsellModalOpen: (open: boolean) => void
  jobId: number
}

const HiringManagerItem = ({
  hiringManager: hm,
  isCreator,
  currentHiringRole,
  removeHiringManager,
  hasHiringManagerRolesFeature,
  canManageJobPostings,
  setUpsellModalOpen,
  jobId,
  firstItem,
}: HiringManagerItemProps) => {
  const { openDialog } = useConfirmationDialog()
  const { access } = useCurrentSession()

  const [hiringRole, setHiringRole] = useState<AtsHiringRolesEnum>(hm.hiringRole ?? 'editor')
  const [updateHiringManagerRole, { loading: updatingHiringManagerRole }] = useMutation(
    AtsUpdateHiringManagerRoleDocument,
    {
      variables: {
        id: hm.id,
        hiringRole,
      },
      extractorFn: (data) => data.atsMutations.updateAtsHiringManager,
      refetchQueries: [AtsGetHiringTeamDocument],
    }
  )

  const handleUpdateHiringManagerRole = async (value: AtsHiringRolesEnum) => {
    const oldHiringRole = hiringRole
    setHiringRole(value)
    const result = await updateHiringManagerRole({
      variables: {
        id: hm.id,
        hiringRole: value,
      },
    })

    const handled = handleResult(result, () => {})

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })

      track(trackingEvents.ats.hiringRoleEdited, {
        new_hiring_role: hiringRole,
        old_hiring_role: oldHiringRole,
        user_id: hm.access?.id,
        ats_job_posting_id: jobId,
      })
    } else {
      setHiringRole(oldHiringRole)

      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.save_error'),
      })
    }

    return handled
  }

  const isCurrentUser = access?.id === hm.access?.id

  const enableRoleSelection =
    (canManageJobPostings || currentHiringRole !== 'reviewer') && !isCurrentUser

  const canBeRemoved = (canManageJobPostings || !isCreator) && !isCurrentUser

  return (
    <Box
      padding="s16"
      width="full"
      alignItems="center"
      justifyContent="spaceBetween"
      flexDirection="row"
      border={{
        top: firstItem
          ? undefined
          : {
              width: 's1',
              style: 'solid',
              color: 'grey400',
            },
      }}
    >
      <Box alignItems="center" flexDirection="row" gap="s16">
        <F0AvatarPerson
          src={buildUrl(hm.access?.avatar || undefined)}
          size="medium"
          firstName={hm.access?.firstName || ''}
          lastName={hm.access?.lastName || ''}
        />
        <Box flexDirection="column" gap="s4">
          <Text.Normal weight="medium" color="grey1000">
            {hm.access?.firstName} {hm.access?.lastName}
          </Text.Normal>
          <Text.Normal color="grey800">
            {hm.access?.employee?.contractVersions.at(-1)?.jobTitle || ''}
          </Text.Normal>
        </Box>
      </Box>

      <Box alignItems="center" flexDirection="row" gap="s8">
        {isCreator && (
          <F0TagStatus
            text={i18n.t('ats.job_posting_settings.hiring_team.roles.owner_tag_label')}
            variant="info"
          />
        )}

        <F0Select
          label={i18n.t('ats.job_posting_settings.hiring_team.roles.hiring_manager.label')}
          hideLabel
          options={[
            {
              label: i18n.t('ats.job_posting_settings.hiring_team.roles.recruiter.label'),
              description: i18n.t(
                'ats.job_posting_settings.hiring_team.roles.recruiter.description'
              ),
              value: 'owner',
              icon: Crown,
            },
            {
              label: i18n.t('ats.job_posting_settings.hiring_team.roles.hiring_manager.label'),
              description: i18n.t(
                'ats.job_posting_settings.hiring_team.roles.hiring_manager.description'
              ),
              value: 'editor',
              icon: Suitcase,
            },
            {
              label: i18n.t('ats.job_posting_settings.hiring_team.roles.reviewer.label'),
              description: i18n.t(
                'ats.job_posting_settings.hiring_team.roles.reviewer.description'
              ),
              value: 'reviewer',
              icon: hasHiringManagerRolesFeature ? Person : LockLocked,
            },
          ]}
          onChange={(value: AtsHiringRolesEnum) => {
            if (value === 'reviewer' && !hasHiringManagerRolesFeature) {
              setUpsellModalOpen(true)
            } else {
              handleUpdateHiringManagerRole(value)
            }
          }}
          value={hm.hiringRole}
          disabled={
            updatingHiringManagerRole || (firstItem ? !canManageJobPostings : !enableRoleSelection)
          }
        />
        {canBeRemoved && (
          <F0Button
            variant="outline"
            onClick={() =>
              openDialog({
                title: i18n.t('ats.job_posting_settings.hiring_team.delete_modal.title'),
                description: i18n.t(
                  'ats.job_posting_settings.hiring_team.delete_modal.description'
                ),
                onConfirm: () => removeHiringManager(hm.id),
                confirmLabel: i18n.t('ats.job_posting_settings.hiring_team.delete_modal.confirm'),
                cancelLabel: i18n.t('common.cancel'),
              })
            }
            icon={Delete}
            label={i18n.t('ats.job_posting_settings.hiring_team.remove_hiring_manager')}
            hideLabel
          />
        )}
      </Box>
    </Box>
  )
}

export const HiringManagerInfo = ({
  hiringManagers,
  removeHiringManager,
  hasHiringManagerRolesFeature,
  canManageJobPostings,
  currentHiringRole,
  jobId,
}: Props) => {
  const [upsellModalOpen, setUpsellModalOpen] = useState(false)

  return (
    <Box
      border={{
        all: {
          width: 's1',
          style: 'solid',
          color: 'grey400',
        },
      }}
      borderRadius={{ all: 'abs016' }}
    >
      {hiringManagers.map((hiringManager, index) => (
        <HiringManagerItem
          key={index}
          hiringManager={hiringManager}
          isCreator={index === 0}
          currentHiringRole={currentHiringRole}
          removeHiringManager={removeHiringManager}
          firstItem={index === 0}
          hasHiringManagerRolesFeature={hasHiringManagerRolesFeature}
          canManageJobPostings={canManageJobPostings}
          setUpsellModalOpen={setUpsellModalOpen}
          jobId={jobId}
        />
      ))}

      {upsellModalOpen && (
        <UpsellingKit.ProductModal
          isOpen
          onClose={() => setUpsellModalOpen(false)}
          moduleRequested={AvailableModules.ats}
          modalTitle={i18n.t('sidebar.recruitment')}
          image={UpsellImage}
          benefits={[
            i18n.t('ats.job_posting_settings.hiring_team.upsell_modal.benefits.first'),
            i18n.t('ats.job_posting_settings.hiring_team.upsell_modal.benefits.second'),
            i18n.t('ats.job_posting_settings.hiring_team.upsell_modal.benefits.third'),
            i18n.t('ats.job_posting_settings.hiring_team.upsell_modal.benefits.fourth'),
          ]}
          title={i18n.t('ats.job_posting_settings.hiring_team.upsell_modal.title')}
          pageSource="ats"
          requestSource="Hiring team - reviewer"
          requestReason={ValidReasons.MissingFeature}
          featureRequested={Features.ats_customized_features}
        />
      )}
    </Box>
  )
}
